import { ArticleEntity, ArticleSchema } from 'mk2/apps/blogs/schemas';
import { TestingStatus } from 'mk2/apps/bo/schemas';
import { AST, ASTLinkedObjectsData } from 'mk2/apps/wiki/schemas';
import { createLikeableSchema, LikeableEntity } from 'mk2/containers/Like/Like.schemas';
import { Entity, PhotoEntity, PhotoSchema, UserEntity, UserSchema } from 'mk2/schemas';
import { schema } from 'normalizr';

export type PageRef = number | 'last';

export interface CategoryEntity extends Entity {
    orderIndex: number;
    name: string;
    slug: string;
    coverPhoto: PhotoEntity;
    id: number;
}

export interface TopicEntity extends Entity {
    slug: string;
    category: CategoryEntity;
    name: string;
    description: string;
    moderators: UserEntity[];
    coverPhoto: PhotoEntity;
    isCommercial: boolean;
    isBrandDays: boolean;
    deletedAt: string;
    deletedReason: string;
    deletedBy: UserEntity;
    numRecentContributors: number;
    numThreads: number;
    visibility: number;
    created: string;
}

export interface TopicModeratorInvitationEntity extends Entity {
    invitee: UserEntity;
    inviter: UserEntity;
    createdAt: string;
    deleteAt: string;
    acceptedAt: string;
}

export interface TopicModeratorsDetailsEntity extends Entity {
    allModerators: Array<{
        user: UserEntity;
        createdAt: string;
        deletedAt: string;
    }>;
    pendingInvitations: TopicModeratorInvitationEntity[];
}

export interface ProductTestingEntity extends Entity {
    name: string;
    isActive: boolean;
    trackedHashtag: string;
    status: TestingStatus;
    teaserTitle: string;
    teaserText: string;
    teaserLink: string;
    callTitle: string;
    callText: string;
    callLink: string;
    callEndDate: string;
    finishedTitle: string;
    finishedText: string;
    finishedLink: string;
    finishedTesters: string;
    photos: PhotoEntity[];

}

export interface PostEntity extends Entity {
    topic: TopicEntity;
    author: UserEntity;
    created: string;
    heading: string;
    perexAST: AST;
    linkedObjects: ASTLinkedObjectsData;
    photos: PhotoEntity[];
    stickerId: number;
    likeable: LikeableEntity;
    comments: number;
    sticky: boolean;
    locked: boolean;
    lastMessageTime: string;
    slug: string;
    popularAddedOn: string;
    noindex: boolean;
    isNoAds: boolean;
    canBeDeleted: boolean;
    deletedAt?: string;
    deletedByAuthor?: boolean;
    deletedReason?: string;
    deletedBy?: UserEntity;
    counsellingAdminApprovedTime: string;
    testing?: ProductTestingEntity;
    contestEnded?: boolean;
    isMyAnonymousPost: boolean;
}

export interface ContestEntity extends Entity {
    name: string;
    isDraft: boolean;
    endDate: string;
    seeAnswers: boolean;
    postId: number;
}

export interface BrandDaysCampaignEntity extends Entity {
    name: string;
    title: string;
    user: UserEntity;
    logoUrl: string;        // kam ma smerovat preklik z loga
    logoSrc: string;        // url kde je obrazok
    logoWidth: number;      // rozmery loga (dlzka), zobrazi sa do obdlznika 80x30
    logoHeight: number;     // rozmery loga (sirka), zobrazi sa do obdlznika 80x30
    logoPrefix: string;     // text co sa zobrazi pred logom
    forumTopicSlug: string;
}

export interface CounsellingEntity extends Entity {
    counsellor: UserEntity;
    counsellorAvatar: string;
    counsellorAvatarPhoto: PhotoEntity;
    counsellorName: string;
    counsellorSignature: string;
    isLocked: boolean;
    isArchived: boolean;
    lockedMessage: string;
    topic: TopicEntity;
}

export interface ArchivedCampaignsEntity extends Entity {
    topic: TopicEntity;
    date: Date;
    isActive: boolean;
}

export const CategorySchema = new schema.Entity('categories', {
    coverPhoto: PhotoSchema,
});

export const TopicSchema = new schema.Entity('topics', {
    category: CategorySchema,
    moderators: [UserSchema],
    coverPhoto: PhotoSchema,
    deletedBy: UserSchema,
});

export const TopicModeratorInvitationSchema = new schema.Entity('topicModeratorInvitations', {
    invitee: UserSchema,
    inviter: UserSchema,
});

export const TopicModeratorsDetailsSchema = new schema.Entity('topicModeratorsDetails', {
    allModerators: [new schema.Object({
        user: UserSchema,
    })],
    pendingInvitations: [TopicModeratorInvitationSchema],
});

export const MicroPostSchema = new schema.Entity('microPosts', {
});

export const ProductTestingSchema = new schema.Entity('productTesting', {
    photos: [PhotoSchema],
});

export const PostSchema = new schema.Entity('posts', {
    topic: TopicSchema,
    author: UserSchema,
    photos: [PhotoSchema],
    likeable: createLikeableSchema('thread'),
    deletedBy: UserSchema,
    testing: ProductTestingSchema,
});

export const ContestSchema = new schema.Entity('contests');

export const BrandDaysCampaignSchema = new schema.Entity('brandDaysCampaigns', {
    user: UserSchema,
});

export const CounsellingSchema = new schema.Entity('counsellings', {
    topic: TopicSchema,
    counsellor: UserSchema,
    counsellorAvatarPhoto: PhotoSchema,
});

export const ArchivedCampaignsSchema = new schema.Entity('archivedCampaigns', {
    topic: TopicSchema,
});

// TODO Refactor, because it's confusing for most of us, it's like Comment Schema
// TODO but because Like needs entity matched with DB, we need to write new schema
export const MessageSchema = new schema.Entity('messages', {
    authorUser: UserSchema,
    targetOwner: UserSchema,
    photos: [PhotoSchema],
    likeable: createLikeableSchema('forum-message'),
});

export interface ComplaintsStatistics {
    count: number;
    topicId: number;
}
export interface ComplaintsStatisticsPerPost {
    [postId: number]: ComplaintsStatistics;
}
export interface ComplaintsCountPerTopic {
    [topicId: number]: number;
}
